import { GPSTrack, SearchPredicate } from '@nx-smartmonkey/shared/domain';
import { PaginatedResult } from '@nx-smartmonkey/shared/interfaces';

import { Driver } from '../../domain/driver/Driver';
import { DriverStatus } from '../../domain/driver/DriverStatus';
import { GeoFence } from '../../domain/geoFence/GeoFence';
import { PlanFlat } from '../../domain/plan/PlanFlat';
import { Stop } from '../../domain/stop/Stop';
import { MapFilters, OptimizationData } from './reducer';

// eslint-disable-next-line no-shadow
export enum PlanActionType {
  SET_SEARCH_PLANS_PREDICATES = `SET_SEARCH_PLANS_PREDICATES`,
  SEARCH_PLANS_SUCCESS = `SEARCH_PLANS_SUCCESS`,
  SEARCH_FLAT_PLANS_SUCCESS = `SEARCH_FLAT_PLANS_SUCCESS`,
  SET_ACTIVE_PLAN = `SET_ACTIVE_PLAN`,
  SET_PROJECT_PLANS_TABLE_COLUMNS = `SET_PROJECT_PLANS_TABLE_COLUMNS`,
  SET_ACTIVE_PLAN_DRIVERS = `SET_ACTIVE_PLAN_DRIVERS`,
  SET_ACTIVE_PLAN_STOPS = `SET_ACTIVE_PLAN_STOPS`,
  SET_ACTIVE_PLAN_GEO_FENCES = `SET_ACTIVE_PLAN_GEO_FENCES`,
  CREATE_PLAN_STOP = `CREATE_PLAN_STOP`,
  UPDATE_PLAN_STOP = `UPDATE_PLAN_STOP`,
  RESCHEDULE_PLAN_STOP = `RESCHEDULE_PLAN_STOP`,
  DELETE_PLAN_STOP = `DELETE_PLAN_STOP`,
  CREATE_PLAN_DRIVER = `CREATE_PLAN_DRIVER`,
  UPDATE_PLAN_DRIVER = `UPDATE_PLAN_DRIVER`,
  PLAN_STOP_RESCHEDULED = `PLAN_STOP_RESCHEDULED`,
  UPDATE_PLAN_DRIVER_LAST_POSITION = `UPDATE_PLAN_DRIVER_LAST_POSITION`,
  UPDATE_PLAN_DRIVER_STATUS = `UPDATE_PLAN_DRIVER_STATUS`,
  DELETE_PLAN_DRIVER = `DELETE_PLAN_DRIVER`,
  SET_ACTIVE_PLAN_MAP_FILTERS = `SET_ACTIVE_PLAN_MAP_FILTERS`,
  SET_ACTIVE_PLAN_OPTIMIZATION_DATA = `SET_ACTIVE_PLAN_OPTIMIZATION_DATA`,
  SET_ACTIVE_PLAN_EDITING_GEO_FENCE = `SET_ACTIVE_PLAN_EDITING_GEO_FENCE`,
  SET_ACTIVE_PLAN_FOCUSED_STOP = `SET_ACTIVE_PLAN_FOCUSED_STOP`,
}

export interface SetSearchPlansPredicatesAction {
  type: PlanActionType.SET_SEARCH_PLANS_PREDICATES;
  payload: {
    predicates: Array<SearchPredicate>;
    predicatesBase64: string;
  };
}

export interface SearchPlansSuccessAction {
  type: PlanActionType.SEARCH_PLANS_SUCCESS;
  payload: {
    plans: PaginatedResult<PlanFlat>;
  };
}

export interface SearchFlatPlansSuccessAction {
  type: PlanActionType.SEARCH_FLAT_PLANS_SUCCESS;
  payload: {
    flatPlans: Array<PlanFlat>;
  };
}

export interface SetActivePlanAction {
  type: PlanActionType.SET_ACTIVE_PLAN;
  payload: {
    activePlan?: PlanFlat;
  };
}

export interface SetProjectPlansTableColumnsAction {
  type: PlanActionType.SET_PROJECT_PLANS_TABLE_COLUMNS;
  payload: Array<any>;
}

export interface SetActivePlanDriversAction {
  type: PlanActionType.SET_ACTIVE_PLAN_DRIVERS;
  payload: {
    planDrivers: Array<Driver>;
  };
}

export interface SetActivePlanStopsAction {
  type: PlanActionType.SET_ACTIVE_PLAN_STOPS;
  payload: {
    planStops: Array<Stop>;
  };
}

export interface SetActivePlanGeoFencesAction {
  type: PlanActionType.SET_ACTIVE_PLAN_GEO_FENCES;
  payload: {
    planGeoFences: Array<GeoFence>;
  };
}

export interface CreatePlanStopAction {
  type: PlanActionType.CREATE_PLAN_STOP;
  payload: {
    stop: Stop;
  };
}

export interface UpdatePlanStopAction {
  type: PlanActionType.UPDATE_PLAN_STOP;
  payload: {
    stop: Stop;
  };
}

export interface ReschedulePlanStopAction {
  type: PlanActionType.RESCHEDULE_PLAN_STOP;
  payload: {
    stop: Stop;
  };
}

export interface DeletePlanStopAction {
  type: PlanActionType.DELETE_PLAN_STOP;
  payload: {
    stop: Stop;
  };
}

export interface CreatePlanDriverAction {
  type: PlanActionType.CREATE_PLAN_DRIVER;
  payload: {
    driver: Driver;
  };
}

export interface UpdatePlanDriverAction {
  type: PlanActionType.UPDATE_PLAN_DRIVER;
  payload: {
    driver: Driver;
  };
}

export interface PlanStopRescheduledAction {
  type: PlanActionType.PLAN_STOP_RESCHEDULED;
  payload: {
    stop: Stop;
  };
}

export interface UpdatePlanDriverLastPositionAction {
  type: PlanActionType.UPDATE_PLAN_DRIVER_LAST_POSITION;
  payload: {
    driverId: string;
    lastPosition?: GPSTrack;
  };
}

export interface UpdatePlanDriverStatusAction {
  type: PlanActionType.UPDATE_PLAN_DRIVER_STATUS;
  payload: {
    status: DriverStatus;
    driverId: string;
  };
}

export interface DeletePlanDriverAction {
  type: PlanActionType.DELETE_PLAN_DRIVER;
  payload: {
    driver: Driver;
  };
}

export interface SetActivePlanMapFiltersAction {
  type: PlanActionType.SET_ACTIVE_PLAN_MAP_FILTERS;
  payload: {
    mapFilters: MapFilters;
  };
}

export interface SetActivePlanOptimizationDataAction {
  type: PlanActionType.SET_ACTIVE_PLAN_OPTIMIZATION_DATA;
  payload: OptimizationData;
}

export interface SetActivePlanEditingGeoFenceAction {
  type: PlanActionType.SET_ACTIVE_PLAN_EDITING_GEO_FENCE;
  payload: {
    editingGeoFence?: GeoFence;
  };
}

export interface SetActivePlanFocusedStopAction {
  type: PlanActionType.SET_ACTIVE_PLAN_FOCUSED_STOP;
  payload: {
    focusedStop: Stop | undefined;
  };
}

export type PlanActions =
  | SetSearchPlansPredicatesAction
  | SearchPlansSuccessAction
  | SearchFlatPlansSuccessAction
  | SetActivePlanAction
  | SetProjectPlansTableColumnsAction
  | SetActivePlanDriversAction
  | SetActivePlanStopsAction
  | SetActivePlanGeoFencesAction
  | CreatePlanStopAction
  | UpdatePlanStopAction
  | ReschedulePlanStopAction
  | DeletePlanStopAction
  | CreatePlanDriverAction
  | UpdatePlanDriverAction
  | PlanStopRescheduledAction
  | UpdatePlanDriverLastPositionAction
  | UpdatePlanDriverStatusAction
  | DeletePlanDriverAction
  | SetActivePlanMapFiltersAction
  | SetActivePlanOptimizationDataAction
  | SetActivePlanEditingGeoFenceAction
  | SetActivePlanFocusedStopAction;
